<template>
  <div class="app-page__container">
    <section v-if="!isDashboardDataLoading" class="app-page__header">
      <div class="user-details__container">
        <UserAvatar :user="activeUser" />
        <div class="user-details__name">
          <UserDisplayName :active-user-id="activeUser.id" size="large" />
          <!-- <p class="subtitle is-size-6">{{ activeDate.toLocaleString(undefined, defaultDateOptions) }}</p> -->
        </div>
      </div>
      <DatePicker
        :active-team-ids="getUserTeams(activeUserId, teams).map(team => team.id)"
        @activeDateChanged="handleActiveDateChange"
      />
    </section>

    <div v-if="!isDashboardDataLoading" class="columns is-paddingless is-desktop">
      <div class="column is-7-desktop">
        <section v-if="isActiveUserLoggedInUser && isActiveUserOOO" class="app-page__section">
          <b-message type="is-warning" :closable="true">
            You're marked as out of office today.
            <router-link to="/my-profile">Manage OOO settings</router-link>
          </b-message>
        </section>
        <section class="app-page__section">
          <DailyGoal
            :active-user-id="activeUserId"
            :goal-date="activeDateInISO"
            :is-loading="isNewActiveGoalLoading"
          />
        </section>

        <section class="app-page__section">
          <GoalRecord
            :active-user="activeUser"
          />
        </section>
      </div>
      <div class="column">
        <section class="app-page__section">
          <TeamGoals
            :active-date="activeDate"
            :active-team-ids="getUserTeams(activeUserId, teams).map(team => team.id)"
            :excluded-user-ids="[activeUserId]"
            custom-card-title="My Team's Goals"
          />
        </section>
      </div>
    </div>
    <div v-else style="position: relative; min-height: 150px;">
      <BLoading :is-full-page="false" :active.sync="isDashboardDataLoading" :can-cancel="false"></BLoading>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { defaultDateOptions, dateToISO, isUserOOO } from '@/helpers/dateHelpers';
import { getUserTeams } from '@/helpers/teamHelpers';
import * as UserActions from '@/store/actions/User.actions';
import * as GoalActions from '@/store/actions/Goal.actions';
import * as TeamActions from '@/store/actions/Team.actions';
import UserAvatar from '@/components/UserAvatar.vue';
import GoalRecord from '@/components/GoalRecord.vue';
import TeamGoals from '@/components/TeamGoals.vue';
import DailyGoal from '@/components/DailyGoal.vue';
import UserDisplayName from '@/components/UserDisplayName.vue';
import DatePicker from '@/components/DatePicker.vue';

export default {
  name: 'DashboardView',
  components: {
    DailyGoal,
    GoalRecord,
    UserAvatar,
    TeamGoals,
    UserDisplayName,
    DatePicker,
  },

  data() {
    return {
      todaysGoal: '',
      isEditingTodaysGoal: true,
      isActiveUserLoading: true,
      isTodaysGoalLoading: true,
      isNewActiveGoalLoading: false,
      defaultDateOptions,
      areTeammatesLoading: true,
      areUserGoalsLoading: true,
      activeUserId: null,
      activeDate: new Date(),
    };
  },

  computed: {
    ...mapGetters([
      'loggedInUser',
      'activeWorkspace',
    ]),
    ...mapState({
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
      isSessionAuthLoading: state => state.auth.isSessionAuthLoading,
      isLogInFormLoading: state => state.auth.isLogInFormLoading,
      teams: state => state.team.teams,
      users: state => state.user.users,
    }),
    isDashboardDataLoading() {
      return this.isActiveUserLoading || this.isTodaysGoalLoading;
    },
    activeUser() {
      return this.users[this.activeUserId] || {};
    },
    isActiveUserLoggedInUser() {
      if (!this.activeUserId || !this.loggedInUser.id) return false;

      return this.activeUserId === this.loggedInUser.id;
    },
    activeDateInISO() {
      return dateToISO(this.activeDate);
    },
    isActiveUserOOO() {
      return isUserOOO(this.activeUser, this.activeDateInISO);
    },
  },

  created() {
    this.fetchInitialData();
  },

  watch: {
    '$route'() { // eslint-disable-line object-shorthand
      this.fetchInitialData();
    },
  },

  methods: {
    getUserTeams,
    ...mapActions({
      fetchWorkspaceUsersById: UserActions.FETCH_WORKSPACE_USERS_BY_IDS,
      fetchUserTeams: TeamActions.FETCH_USER_TEAMS,
      fetchPublicUserTeams: TeamActions.FETCH_PUBLIC_USER_TEAMS,
      fetchGoalsByUserIdsByDates: GoalActions.FETCH_GOALS_BY_USER_IDS_BY_DATES,
    }),
    async fetchActiveUserOnLoad() {
      if (this.$route.path === '/dashboard') {
        this.activeUserId = this.loggedInUser.id;
      } else {
        this.activeUserId = this.$route.params.id;
      }

      await this.fetchWorkspaceUsersById({ userIds: [this.activeUserId], workspaceId: this.activeWorkspace.id });
      if (this.isActiveUserLoggedInUser) {
        await this.fetchUserTeams({ userId: this.activeUserId, workspaceId: this.activeWorkspace.id });
      } else {
        await this.fetchPublicUserTeams({ userId: this.activeUserId, workspaceId: this.activeWorkspace.id });
      }
    },
    async fetchInitialData() {
      this.isActiveUserLoading = true;
      await this.fetchActiveUserOnLoad();
      this.isActiveUserLoading = false;

      this.isTodaysGoalLoading = true;
      await this.fetchGoalsByUserIdsByDates({
        userIds: [this.activeUserId],
        dates: [this.activeDateInISO],
        workspaceId: this.activeWorkspace.id,
      });
      this.isTodaysGoalLoading = false;
    },
    async handleActiveDateChange(newDate) {
      this.activeDate = newDate;
      this.isNewActiveGoalLoading = true;
      await this.fetchGoalsByUserIdsByDates({
        userIds: [this.activeUserId],
        dates: [this.activeDateInISO],
        workspaceId: this.activeWorkspace.id,
      });
      this.isNewActiveGoalLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.user-details__container {
  display: flex;
}

.user-details__name {
  margin-left: $global-whitespace-small;
  align-self: center;
}

.user-name {
  align-self: center;
  margin-left: $global-whitespace-small;
}

.content-wrapper {
  display: flex;
}
</style>
